import type { SocialAuthProvider } from "@pax/api-contract/client-config";
import { queryOptions } from "@tanstack/react-query";
import { UNREACHABLE_MESSAGE } from "@/lib/api";
import { authClient } from "@/lib/auth-client";

export const PROVIDER_NAMES: Record<SocialAuthProvider, string> = {
  google: "Google",
  apple: "Apple",
  wechat: "WeChat",
};

/** Each social provider the signed-in account signs in with, and Better Auth's id for that link. */
export const linkedAccountsQuery = queryOptions({
  queryKey: ["linked-sign-in-methods"],
  queryFn: async (): Promise<{ provider: SocialAuthProvider; accountId: string }[]> => {
    const { data, error } = await authClient.listAccounts();
    if (error) throw new Error(error.message || UNREACHABLE_MESSAGE);
    return data.flatMap(linked =>
      linked.providerId in PROVIDER_NAMES
        ? [{ provider: linked.providerId as SocialAuthProvider, accountId: linked.id }]
        : [],
    );
  },
});

/** The social providers the signed-in account signs in with. */
export const linkedSignInMethodsQuery = queryOptions({
  ...linkedAccountsQuery,
  select: accounts => accounts.map(linked => linked.provider),
});

/** Every signed-in browser and app on the account, newest first. */
export const signedInSessionsQuery = queryOptions({
  queryKey: ["signed-in-sessions"],
  queryFn: async () => {
    const { data, error } = await authClient.listSessions();
    if (error) throw new Error(error.message || UNREACHABLE_MESSAGE);
    return [...data].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  },
});

export type SignedInSession = Awaited<
  ReturnType<NonNullable<typeof signedInSessionsQuery.queryFn>>
>[number];

/** What a session signed in from, as a person would say it: "Chrome on macOS", or "Pax for iOS". */
export function sessionDeviceName(session: { userAgent?: string | null; platform?: unknown }) {
  if (session.platform === "ios") return "Pax for iOS";
  if (session.platform === "cli") return "Pax command line";
  const agent = session.userAgent ?? "";
  const browser = /Edg\//.test(agent)
    ? "Edge"
    : /Firefox\//.test(agent)
      ? "Firefox"
      : /Chrome\//.test(agent)
        ? "Chrome"
        : /Safari\//.test(agent)
          ? "Safari"
          : "A browser";
  const system = /iPhone|iPad/.test(agent)
    ? "iOS"
    : /Android/.test(agent)
      ? "Android"
      : /Mac OS X/.test(agent)
        ? "macOS"
        : /Windows/.test(agent)
          ? "Windows"
          : /Linux/.test(agent)
            ? "Linux"
            : null;
  return system ? `${browser} on ${system}` : browser;
}

/** Whether a session is on a phone, for its icon. */
export function sessionOnPhone(session: { userAgent?: string | null; platform?: unknown }) {
  return session.platform === "ios" || /iPhone|Android/.test(session.userAgent ?? "");
}
